import type { RollupCategory } from '../generated/enums.js';

/**
 * Cross-CoA consolidated reporting (ALC-2000).
 *
 * The group report takes a **set** of `(entity, book)` scopes plus a reporting currency and
 * aggregates by consolidation **rollup group**, never by physical `glAccountId`. Two entities
 * on different charts land on the same output row because their accounts map to the same
 * rollup group in their own CoA versions; two entities on the same chart but different
 * functional currencies are translated before they are added together.
 *
 * It reports a true consolidation, not a combination: a scope set that still carries an open
 * intercompany position is refused, because this report has no counterparty data with which to
 * eliminate intragroup balances and results.
 *
 * There is deliberately no single-book / single-currency variant of this report: the previous
 * `bookId + periodId` shape could only ever consolidate one book and summed functional-currency
 * balances untranslated, which is the defect this contract replaces.
 *
 * Each rollup group publishes exactly one translated figure, whose meaning is stated by `measure`:
 * a closing position for balance-sheet groups, this period's movement for income-statement groups.
 * The old shape also carried `opening`, `debits` and `credits`, and those are deliberately gone. A
 * translated roll-forward does not reconcile without a cumulative translation adjustment line —
 * opening sits at the prior period's closing rate, movements at the period average, closing at the
 * period-end rate — and this report has no basis to derive that adjustment, for the same reason
 * foreign equity is refused rather than approximated.
 */

/**
 * One consolidation leg. `bookId` is an **assertion**, not a selector: ALC-1993's
 * one-book-per-entity invariant means the entity already determines its book, and a mismatched
 * `bookId` is rejected by the shared accounting-scope resolver rather than silently ignored.
 */
export interface ConsolidationScopeSelector {
  entityId: string;
  bookId?: string;
}

/**
 * Translation basis, chosen per rollup group from its `RollupCategory` (see
 * `CONSOLIDATION_RATE_TYPE_BY_CATEGORY`). Two types, never one:
 *
 * - `CLOSING` — closing (spot) rate at the scope's period end date. Balance-sheet lines
 *   (CPC 02 / IAS 21.39(a), ASC 830-30-45-3).
 * - `AVERAGE` — average rate for the scope's period, keyed on the period end date. Income
 *   statement lines (CPC 02 / IAS 21.39(b), ASC 830-30-45-3), the customary practical
 *   expedient for the rate at each transaction date.
 */
export const CONSOLIDATION_RATE_TYPES = ['CLOSING', 'AVERAGE'] as const;

export type ConsolidationRateType = (typeof CONSOLIDATION_RATE_TYPES)[number];

/** The report's rate rule, stated once so callers and tests read the same table. */
export const CONSOLIDATION_RATE_TYPE_BY_CATEGORY: Readonly<
  Record<RollupCategory, ConsolidationRateType>
> = Object.freeze({
  BALANCE_SHEET: 'CLOSING',
  INCOME_STATEMENT: 'AVERAGE',
});

/**
 * What the published figure measures. The rate type alone does not say this, and getting it wrong
 * is a silent misstatement:
 *
 * - `CLOSING_POSITION` — the balance carried at the period end. Correct for balance-sheet lines,
 *   which are a position at a point in time and translate at that date's closing rate.
 * - `PERIOD_MOVEMENT` — the movement recorded in this period alone (`closing − opening`). Income
 *   statement lines accumulate across the year, so translating a cumulative balance at the current
 *   period's average rate would re-express earlier periods' results at a rate that never applied to
 *   them. Only the period's own movement belongs at the period's own average rate.
 */
export const CONSOLIDATION_MEASURES = ['CLOSING_POSITION', 'PERIOD_MOVEMENT'] as const;

export type ConsolidationMeasure = (typeof CONSOLIDATION_MEASURES)[number];

export const CONSOLIDATION_MEASURE_BY_CATEGORY: Readonly<
  Record<RollupCategory, ConsolidationMeasure>
> = Object.freeze({
  BALANCE_SHEET: 'CLOSING_POSITION',
  INCOME_STATEMENT: 'PERIOD_MOVEMENT',
});

export interface ConsolidatedRollupQuery {
  scopes: ConsolidationScopeSelector[];
  /**
   * `YYYY-MM-DD`. Each scope resolves this date to *its own* period and CoA version, so a
   * group whose entities label periods differently still consolidates. A period label is
   * deliberately not accepted: it would assume label alignment across charts.
   */
  accountingDate: string;
  /** ISO-4217 code every contributing balance is translated into. */
  reportingCurrency: string;
}

/** How one scope selector resolved — echoed back so a reader can audit the basis. */
export interface ConsolidatedRollupScopeResolution {
  entityId: string;
  bookId: string;
  periodId: string;
  /** `YYYY-MM-DD`; the first day of the period this scope resolved to. */
  periodStartDate: string;
  /** `YYYY-MM-DD`; the rate date for both `CLOSING` and `AVERAGE` translation of this scope. */
  periodEndDate: string;
  coaVersionId: string;
  frameworkCode: string;
  functionalCurrency: string;
}

/** One scope's translated share of a rollup group row. */
export interface ConsolidatedRollupContribution {
  entityId: string;
  bookId: string;
  periodId: string;
  coaVersionId: string;
  functionalCurrency: string;
  /**
   * A contra account joins its group as a deduction, so its `opening`/`closing` are published
   * negated. `debits`/`credits` stay gross unsigned movement.
   */
  isContra: boolean;
  rateType: ConsolidationRateType;
  /** Units of `reportingCurrency` per one unit of `functionalCurrency`, at 18 decimal places. */
  rate: string;
  /**
   * `IDENTITY` only when `functionalCurrency === reportingCurrency`, where the rate is exactly
   * 1 by definition. A missing rate for a real pair is an error, never an identity fallback.
   */
  rateSource: 'IDENTITY' | 'QUOTE';
  rateDate: string;
  measure: ConsolidationMeasure;
  /**
   * The figure named by `measure`, already translated into `reportingCurrency`, and — like
   * `gl_balance` and the financial-reports rollup aggregator — **natural-balance-positive**: a
   * credit balance on a credit-natural account is a positive number. The one exception is a contra
   * account, whose amount is negated so it reduces its group (accumulated depreciation of 200
   * against gross PP&E of 1,000 publishes 800, not 1,200).
   */
  amount: string;
}

export interface ConsolidatedRollupRow {
  rollupGroupId: string;
  rollupGroupCode: string;
  rollupGroupName: string;
  rollupCategory: RollupCategory;
  /** The single rate type applied to every component of this row. */
  rateType: ConsolidationRateType;
  /** What every component of this row measures. */
  measure: ConsolidationMeasure;
  reportingCurrency: string;
  /** Natural-balance-positive, exactly equal to the sum of `contributions`. */
  amount: string;
  contributions: ConsolidatedRollupContribution[];
}

export interface ConsolidatedRollupReport {
  reportingCurrency: string;
  accountingDate: string;
  scopes: ConsolidatedRollupScopeResolution[];
  rows: ConsolidatedRollupRow[];
}

/**
 * Stable codes. Callers classify on these, never on the message text — the messages name the
 * offending account / version / currency pair and therefore change with the data.
 */
export const CONSOLIDATED_ROLLUP_ERROR_CODES = {
  SCOPES_REQUIRED: 'CONSOLIDATED_ROLLUP_SCOPES_REQUIRED',
  DUPLICATE_SCOPE: 'CONSOLIDATED_ROLLUP_DUPLICATE_SCOPE',
  INVALID_ACCOUNTING_DATE: 'CONSOLIDATED_ROLLUP_INVALID_ACCOUNTING_DATE',
  PERIOD_NOT_FOUND: 'CONSOLIDATED_ROLLUP_PERIOD_NOT_FOUND',
  REPORTING_CURRENCY_UNSUPPORTED: 'CONSOLIDATED_ROLLUP_REPORTING_CURRENCY_UNSUPPORTED',
  INSUFFICIENT_VISIBILITY: 'CONSOLIDATED_ROLLUP_INSUFFICIENT_VISIBILITY',
  ORPHANED_BALANCE: 'CONSOLIDATED_ROLLUP_ORPHANED_BALANCE',
  ACCOUNT_UNMAPPED: 'CONSOLIDATED_ROLLUP_ACCOUNT_UNMAPPED',
  INTRAGROUP_BALANCE_PRESENT: 'CONSOLIDATED_ROLLUP_INTRAGROUP_BALANCE_PRESENT',
  EQUITY_TRANSLATION_UNSUPPORTED: 'CONSOLIDATED_ROLLUP_EQUITY_TRANSLATION_UNSUPPORTED',
  CATEGORY_MISMATCH: 'CONSOLIDATED_ROLLUP_CATEGORY_MISMATCH',
  RATE_UNAVAILABLE: 'CONSOLIDATED_ROLLUP_RATE_UNAVAILABLE',
} as const;

export type ConsolidatedRollupErrorCode =
  (typeof CONSOLIDATED_ROLLUP_ERROR_CODES)[keyof typeof CONSOLIDATED_ROLLUP_ERROR_CODES];
